import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTULO_FORMA_DE_FECHAMENTO, totalDaComanda } from '@arena/shared/schemas';
import { dinheiro } from '../agenda/formatos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Botao, Feed, ItemDeFeed, Status, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { rotuloDoConsumo } from '../planos/formatos.js';
import { comandaEmAberto } from './formatos.js';
import { ModalDeComanda } from './ModalDeComanda.jsx';

/**
 * A aba **Comandas** de `#m-aluno` — o consumo desta pessoa no bar.
 *
 * O protótipo mostra aqui uma linha de julho: *"1 Coca-Cola Zero · 1
 * água · R$ 12,00 · lançada na mensalidade"*. É a única tela em que o
 * histórico de comandas de alguém aparece junto, e é o que responde "o
 * que entrou na minha mensalidade?" sem abrir o financeiro.
 *
 * É também onde a comanda DO ALUNO se abre pela ficha: "Abrir comanda"
 * quando ele não tem nenhuma aberta, "Ver comanda aberta" quando tem (o
 * servidor admite uma só por pessoa). A comanda recém-aberta já aparece
 * no modal, pronta para receber itens.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export function AbaComandasDoAluno({ alunoId }: { alunoId: string }) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();
  const [aberta, setAberta] = useState<string | null>(null);

  const comandas = useQuery({
    queryKey: ['comandas', 'do-aluno', alunoId],
    queryFn: () => estoqueApi.comandasDoAluno(alunoId),
    enabled: pode('comanda', 'ler'),
  });

  const abrir = useMutation({
    mutationFn: () => estoqueApi.abrirComanda({ alunoId }),
    onSuccess: async (nova) => {
      await cliente.invalidateQueries({ queryKey: ['comandas'] });
      avisar('Comanda aberta.');
      setAberta(nova.id);
    },
    onError: async (e: unknown) => {
      // O 409 de "já tem comanda aberta" costuma ser lista atrasada:
      // atualizar a aba troca o botão por "Ver comanda aberta".
      avisar(mensagemDoErro(e), 'aviso');
      await cliente.invalidateQueries({ queryKey: ['comandas'] });
    },
  });

  if (!pode('comanda', 'ler')) {
    return <Vazio>Seu perfil não vê as comandas.</Vazio>;
  }
  if (comandas.isPending) return <EsqueletoDeCard linhas={3} />;
  if (comandas.isError) {
    return (
      <Erro detalhe={mensagemDoErro(comandas.error)} onTentarDeNovo={() => void comandas.refetch()} />
    );
  }

  const emAberto = comandaEmAberto(comandas.data);

  return (
    <>
      {emAberto ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Botao variante="contorno" pequeno onClick={() => setAberta(emAberto.id)}>
            Ver comanda aberta
          </Botao>
        </div>
      ) : (
        pode('comanda', 'abrir') && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Botao pequeno icone="plus" onClick={() => abrir.mutate()} disabled={abrir.isPending}>
              {abrir.isPending ? 'Abrindo…' : 'Abrir comanda'}
            </Botao>
          </div>
        )
      )}

      {comandas.data.length === 0 && <Vazio>Esta pessoa ainda não consumiu no bar.</Vazio>}

      <Feed>
        {comandas.data.map((comanda) => {
          const total = comanda.total ?? totalDaComanda(comanda.itens);
          const itens = comanda.itens
            .map((item) => `${item.quantidade}× ${item.produto.nome}`)
            .join(' · ');

          return (
            <ItemDeFeed
              key={comanda.id}
              marca={
                <span
                  className="sq"
                  style={{ background: comanda.fechadaEm ? 'var(--panel-3)' : 'var(--peach)' }}
                  aria-hidden="true"
                />
              }
              titulo={
                <>
                  {new Date(comanda.abertaEm).toLocaleDateString('pt-BR')}
                  {comanda.canceladaEm ? ' · cancelada' : ''}
                </>
              }
              subtitulo={itens || 'sem itens'}
              onClick={() => setAberta(comanda.id)}
              direita={
                <>
                  <b className="num">{dinheiro(total)}</b>
                  <small>
                    {comanda.canceladaEm ? (
                      'cancelada'
                    ) : comanda.formaPagamento ? (
                      <>
                        {ROTULO_FORMA_DE_FECHAMENTO[comanda.formaPagamento].toLowerCase()}
                        {rotuloDoConsumo(comanda.consumo) && (
                          <> · {rotuloDoConsumo(comanda.consumo)}</>
                        )}
                      </>
                    ) : (
                      <Status situacao="atencao">Aberta</Status>
                    )}
                  </small>
                </>
              }
            />
          );
        })}
      </Feed>

      {aberta && <ModalDeComanda comandaId={aberta} onFechar={() => setAberta(null)} />}
    </>
  );
}
